/**
 * `<CompletenessMeter>` — how many of the 11 analytics variables are recorded.
 *
 * This is the mechanism that turns outcome capture from a thing people forget
 * into a worklist: a row that reads "2/11" is visibly unfinished, and paired
 * with the "Needs data" filter it gives a recruiter a finite, shrinking pile
 * rather than an open-ended chore.
 *
 * Colour tracks the same three-band family as fit (weak → moderate → strong) so
 * the ledger reads with one palette, and the number is always shown — the bar
 * alone would be a vague impression where an exact count is available.
 */
import type { JSX } from "react";

interface CompletenessMeterProps {
  filled: number;
  total: number;
}

export function CompletenessMeter({ filled, total }: CompletenessMeterProps): JSX.Element {
  const safeTotal = total > 0 ? total : 1;
  const ratio = Math.max(0, Math.min(1, filled / safeTotal));
  const complete = filled >= total;
  const colorVar =
    complete ? "--strong" : ratio >= 0.5 ? "--moderate" : filled === 0 ? "--weak" : "--moderate";

  return (
    <span
      className="inline-flex flex-col gap-[4px]"
      title={`${filled} of ${total} outcome fields recorded`}
      data-testid="ledger-completeness"
    >
      <span className="tabular-nums text-[12px] text-text-secondary">
        {filled}/{total}
      </span>
      <span
        className="block overflow-hidden rounded-full bg-surface-subtle"
        style={{ width: "62px", height: "4px" }}
        role="img"
        aria-label={`${filled} of ${total} outcome fields recorded`}
      >
        <span
          className="block h-full rounded-full"
          style={{
            width: `${ratio * 100}%`,
            background: `hsl(var(${colorVar}))`,
          }}
        />
      </span>
    </span>
  );
}
